/**
 * SAIT-110 (2026-09-24). Состояние нового мобильного меню в общем ядре.
 * В ядре нет импорта самого компонента: расширение загружается по клику на
 * бургер или «Каталог» нижней панели, на десктопе его JS/CSS не запрашиваются.
 */
import { reactive, defineAsyncComponent, nextTick } from 'ui.vue3';
import { closeSearch, cancelPendingOpen } from '../search/search-state';

// Три имени, которые связывают ядро с группой: совпадают с extensions.json,
// bundle.config.js (namespace) и экспортом в mobilemenu/src/index.js.
const EXTENSION = 'vpl.tas.mobilemenu';
const NAMESPACE = 'BX.Vpl.MobileMenu';
const COMPONENT = 'MobileMenuPanel';

export const mobileMenuState = reactive({ open: false, pending: false, entry: 'root' });

let loadPromise = null;
let openingId = 0;
let opener = null;

export function preloadMobileMenu() {
    if (!loadPromise) {
        // ядра Битрикса нет (ошибка порядка скриптов) — отклонённое обещание
        // с понятным текстом вместо исключения в обработчике нажатия
        if (typeof BX === 'undefined' || !BX.Runtime || typeof BX.Runtime.loadExtension !== 'function') {
            return Promise.reject(new Error('vpl.tas: BX.Runtime.loadExtension недоступен'));
        }
        loadPromise = BX.Runtime.loadExtension(EXTENSION).catch((error) => {
            loadPromise = null;
            throw error;
        });
    }
    return loadPromise;
}

/** компонент панели, если группа уже в браузере; до её загрузки — null */
function loadedPanel() {
    try {
        const group = BX.getClass(NAMESPACE);
        return group && group[COMPONENT] ? group[COMPONENT] : null;
    } catch (e) {
        return null;
    }
}

export function openMobileMenu(entry = 'root', element = null) {
    closeSearch();
    opener = element || document.activeElement;
    mobileMenuState.entry = entry === 'catalog' ? 'catalog' : 'root';
    const current = ++openingId;
    if (loadedPanel()) {
        mobileMenuState.pending = false;
        mobileMenuState.open = true;
        return;
    }
    mobileMenuState.pending = true;
    preloadMobileMenu().then(() => {
        if (current !== openingId) {
            return;
        }
        mobileMenuState.pending = false;
        mobileMenuState.open = true;
    }).catch(() => {
        if (current !== openingId) {
            return;
        }
        mobileMenuState.pending = false;
        // Открываем и при отказе: обёртка MobileMenuPanelAsync повторит
        // загрузку сама и при неудаче покажет сообщение об ошибке.
        mobileMenuState.open = true;
    });
}

export function closeMobileMenu(restoreFocus = true) {
    ++openingId;
    mobileMenuState.pending = false;
    // Поиск, который ещё загружается, не должен открыться поверх закрытого меню.
    cancelPendingOpen();
    mobileMenuState.open = false;
    const target = opener;
    opener = null;
    if (restoreFocus && target && typeof target.focus === 'function') {
        nextTick(() => {
            if (target.isConnected) {
                target.focus();
            }
        });
    }
}

export const MobileMenuPanelAsync = defineAsyncComponent({
    loader: () => {
        const panel = loadedPanel();
        if (panel) {
            return Promise.resolve(panel);
        }
        return preloadMobileMenu().then(() => {
            const component = loadedPanel();
            if (!component) {
                throw new Error(`vpl.tas: не найден ${COMPONENT} в ${EXTENSION}`);
            }
            return component;
        });
    },
    loadingComponent: { render: () => null },
    errorComponent: {
        emits: ['close'],
        template: `<div role="alert" style="position:fixed;inset:0;z-index:1100;background:#fff;padding:24px">
            Не удалось загрузить меню.
            <button type="button" @click="$emit('close')">Закрыть</button>
            <button type="button" @click="reload">Обновить страницу</button>
        </div>`,
        methods: { reload() { window.location.reload(); } },
    },
    delay: 0,
    suspensible: false,
    onError(error, retry, fail, attempts) {
        if (attempts <= 2) {
            retry();
        } else {
            console.error(error);
            fail();
        }
    },
});
